import type { ChartGranularity } from 'corso-admin-ui';
import type { RollingDatePreset } from 'corso-types';
import type { reportGroups } from '../reports/groups';

type ReportGroup = (typeof reportGroups)[number];

/** One report on the Dashboards page. */
export type DashboardSection = {
  [Group in ReportGroup as Group['key']]: {
    group: Group['key'];
    report: Group['reports'][number]['key'];
  };
}[ReportGroup['key']];

export const valueStreams = {
  retained: {
    label: 'Exchanges & store credit',
    description: 'Returns kept in your store, after bonus credit',
    section: { group: 'returns', report: 'revenue' },
  },
  shopNow: {
    label: 'Shop Now',
    description: 'Returns turned into new purchases',
    section: { group: 'returns', report: 'shopNow' },
  },
  recovered: {
    label: 'Fees & return shipping',
    description: 'What customers paid to send items back',
    section: { group: 'returns', report: 'revenue' },
  },
  orderEdits: {
    label: 'Order edits',
    description: 'Added by customers editing their own orders',
    section: { group: 'orderEditing', report: 'overview' },
  },
  protection: {
    label: 'Checkout Plus',
    description: 'Protected order value on your invoices',
    section: { group: 'shippingProtection', report: 'orders' },
  },
} as const satisfies Record<
  string,
  { label: string; description: string; section: DashboardSection }
>;

type ValueStreamKey = keyof typeof valueStreams;

export type ValueStream = {
  key: ValueStreamKey;
  /** Net value for the period, in the store's currency. */
  value: number;
  /** The same window just before. */
  previous: number;
  /** Net value per day, oldest first. */
  daily: readonly number[];
};

export const ImpactKind = {
  customersKept: 'customersKept',
  hoursSaved: 'hoursSaved',
  warrantiesServiced: 'warrantiesServiced',
  ordersMadeRight: 'ordersMadeRight',
  newSubscribers: 'newSubscribers',
  newCustomers: 'newCustomers',
  conversations: 'conversations',
  satisfaction: 'satisfaction',
} as const;

export type ImpactKind = (typeof ImpactKind)[keyof typeof ImpactKind];

export type Impact = {
  kind: ImpactKind;
  value: number;
};

export type ValueHomeData = {
  /** Calendar dates (`yyyy-mm-dd`) the daily series line up with. */
  days: readonly string[];
  preset: RollingDatePreset;
  granularity: ChartGranularity;
  streams: readonly ValueStream[];
  impacts: readonly Impact[];
  /** What Corso kept from the store's invoices over the period, when it has any. */
  planCost: number | null;
};

export const sumOf = (values: readonly number[]) =>
  values.reduce((total, value) => total + value, 0);

// nothing this period reads as unused, not as a 100% drop
export const changeOf = (value: number, previous: number) =>
  value === 0 || previous === 0
    ? null
    : (value - previous) / Math.abs(previous);
